import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'

export type ThemeName = 'mint' | 'ocean' | 'sunset' | 'midnight' | 'rose' | 'forest'

export interface Theme {
  name: ThemeName
  label: string
  accent: string
  accent2: string
  soft: string
  ring: string
  unlockLevel: number
}

export const THEMES: Record<ThemeName, Theme> = {
  mint: { name: 'mint', label: 'Mint', accent: '#34d399', accent2: '#10b981', soft: 'rgba(52,211,153,0.14)', ring: 'rgba(52,211,153,0.35)', unlockLevel: 1 },
  ocean: { name: 'ocean', label: 'Ocean', accent: '#60a5fa', accent2: '#3b82f6', soft: 'rgba(96,165,250,0.14)', ring: 'rgba(96,165,250,0.35)', unlockLevel: 3 },
  sunset: { name: 'sunset', label: 'Sunset', accent: '#fb923c', accent2: '#f97316', soft: 'rgba(251,146,60,0.14)', ring: 'rgba(251,146,60,0.35)', unlockLevel: 5 },
  midnight: { name: 'midnight', label: 'Midnight', accent: '#a78bfa', accent2: '#8b5cf6', soft: 'rgba(167,139,250,0.14)', ring: 'rgba(167,139,250,0.35)', unlockLevel: 8 },
  rose: { name: 'rose', label: 'Rose', accent: '#f472b6', accent2: '#ec4899', soft: 'rgba(244,114,182,0.14)', ring: 'rgba(244,114,182,0.35)', unlockLevel: 10 },
  forest: { name: 'forest', label: 'Forest', accent: '#22c55e', accent2: '#16a34a', soft: 'rgba(34,197,94,0.14)', ring: 'rgba(34,197,94,0.35)', unlockLevel: 15 },
}

export const THEME_ORDER: ThemeName[] = ['mint', 'ocean', 'sunset', 'midnight', 'rose', 'forest']

interface ThemeContextValue {
  theme: Theme
  setTheme: (name: ThemeName) => void
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [themeName, setThemeName] = useState<ThemeName>('mint')
  const theme = THEMES[themeName]

  useEffect(() => {
    const root = document.documentElement
    root.style.setProperty('--accent', theme.accent)
    root.style.setProperty('--accent-2', theme.accent2)
    root.style.setProperty('--accent-soft', theme.soft)
    root.style.setProperty('--accent-ring', theme.ring)
    const meta = document.querySelector('meta[name="theme-color"]')
    if (meta) meta.setAttribute('content', theme.accent)
  }, [theme])

  const setTheme = useCallback((name: ThemeName) => setThemeName(name), [])
  const value = useMemo(() => ({ theme, setTheme }), [theme, setTheme])
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export function useTheme() {
  const ctx = useContext(ThemeContext)
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider')
  return ctx
}
